import { Controller } from 'stimulus'
import CursorController from './cursor-instance'
/**
 * CursorManager - Main controller for managing multiple cursors
 *
 * Usage:
 * <div data-controller="cursor-manager"></div>
 *
 * Values:
 * - autostart: Boolean - Whether to start automatically
 * - debug: Boolean - Enable debug logging
 *
 * Actions:
 * - spawn: Spawn a new cursor
 * - clear: Clear all cursors
 */
export default class CursorManagerController extends Controller {
  static targets = ['container']

  static values = {
    autostart: Boolean,
    debug: Boolean,
  }

  static classes = ['cursor', 'container']

  // Predefined cursor settings
  static CURSOR_PRESETS = {
    names: ['John Doe', 'Jane Smith', 'Alex Chen', 'Sam Wilson', 'Maya Patel', 'Chris Johnson'],
    colors: ['purple', 'blue', 'green', 'orange', 'pink', 'teal'],
  }

  // Active cursors registry
  cursors = new Map()

  // Global cursor counter for unique IDs
  cursorCounter = 0

  connect() {
    // Ensure we have a container
    this.ensureContainer()

    // Set up global event listeners
    this.setupGlobalListeners()

    // Auto-start if enabled
    if (this.autostartValue) {
      this.startDemo()
    }
  }

  disconnect() {
    // Clean up all cursors
    this.clearAllCursors()

    // Remove global listeners
    this.removeGlobalListeners()
  }

  /**
   * Ensure we have a container element for cursors
   */
  ensureContainer() {
    if (!this.hasContainerTarget) {
      const container = document.createElement('div')
      container.classList.add(this.hasContainerClass ? this.containerClass : 'cursor-manager__container')
      container.setAttribute('data-cursor-target', 'container')
      this.element.appendChild(container)
    }
  }

  /**
   * Set up global event listeners for responsiveness
   */
  setupGlobalListeners() {
    this.handleResize = this.handleResize.bind(this)

    // Throttle scroll handler to prevent too frequent updates
    this.handleScroll = this.throttle(this.handleScroll.bind(this), 100)

    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  }

  /**
   * Remove global event listeners
   */
  removeGlobalListeners() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
  }

  /**
   * Handle window resize - update all cursor positions
   */
  handleResize() {
    this.cursors.forEach((cursor) => {
      cursor.updatePosition()
    })
  }

  /**
   * Handle window scroll - update all cursor positions
   */
  handleScroll() {
    // With absolute positioning, we don't need to update positions on scroll
    // The browser handles this automatically
    // Only update if cursor has specific requirements (e.g., recalculating relative positions)
    this.cursors.forEach((cursor) => {
      // Only update if cursor is tracking a target element that might have moved
      if (cursor.lastTargetElement || cursor.config.relativeToTarget) {
        cursor.updatePosition()
      }
    })
  }

  /**
   * Spawn a new cursor
   * @param {Object} options - Cursor configuration
   * @param {string} options.name - Cursor name (optional, random if not provided)
   * @param {string} options.color - Cursor color (optional, random if not provided)
   * @param {Object} options.position - Initial position {x, y} (optional, supports percentages)
   * @param {Array} options.instructions - Initial instructions (optional)
   * @param {boolean} options.anchored - Whether to render cursor inside target element (default: false)
   * @param {string} options.anchoredToTarget - Target element to anchor cursor inside (required if anchored: true)
   * @returns {CursorController} - The spawned cursor controller
   */
  spawn(options = {}) {
    const cursorId = `cursor-${++this.cursorCounter}`

    // Handle different positioning types for spawn
    let spawnPosition = options.position || { x: 100, y: 100 }

    // Get positioning context offset to adjust all positions (not needed for anchored positioning)
    const contextOffset = options.anchored ? { x: 0, y: 0 } : this.getPositioningContextOffset()

    // Handle anchored positioning - cursor is rendered inside target element
    if (options.anchored && options.anchoredToTarget) {
      const targetElement = document.querySelector(options.anchoredToTarget)
      if (targetElement) {
        // For anchored cursors, position is relative to the target element
        spawnPosition = {
          x: options.relativeOffset?.x || 0,
          y: options.relativeOffset?.y || 0,
        }
      } else {
        // Fall back to default position and disable anchored mode
        options.anchored = false
        spawnPosition = { x: 100, y: 100 }
      }
    }
    // Handle relative positioning to a target element (existing behavior)
    else if (options.relativeToTarget) {
      const targetElement = document.querySelector(options.relativeToTarget)
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

        // Get element center position in document coordinates, adjusted for positioning context
        const elementPosition = {
          x: rect.left + scrollLeft + rect.width / 2 - contextOffset.x,
          y: rect.top + scrollTop + rect.height / 2 - contextOffset.y,
        }

        // Apply relative offset
        spawnPosition = {
          x: elementPosition.x + (options.relativeOffset?.x || 0),
          y: elementPosition.y + (options.relativeOffset?.y || 0),
        }
      } else {
        // Fall back to default position
        spawnPosition = { x: 100, y: 100 }
      }
    }
    // Handle percentage-based positioning
    else if (spawnPosition && (typeof spawnPosition.x === 'string' || typeof spawnPosition.y === 'string')) {
      // Convert percentage position to absolute pixels
      const result = { ...spawnPosition }

      if (typeof spawnPosition.x === 'string' && spawnPosition.x.endsWith('%')) {
        const percentage = parseFloat(spawnPosition.x) / 100
        result.x = window.innerWidth * percentage
      }

      if (typeof spawnPosition.y === 'string' && spawnPosition.y.endsWith('%')) {
        const percentage = parseFloat(spawnPosition.y) / 100
        result.y = window.innerHeight * percentage
      }

      spawnPosition = result
    }

    // Set defaults
    const config = {
      id: cursorId,
      name: options.name || this.getRandomName(),
      color: options.color || this.getRandomColor(),
      position: spawnPosition,
      instructions: options.instructions || [],
      ...options,
    }

    // Create cursor element
    const cursorElement = this.createCursorElement(config)

    // Handle anchored positioning - append to target element instead of container
    if (config.anchored && config.anchoredToTarget) {
      const targetElement = document.querySelector(config.anchoredToTarget)
      if (targetElement) {
        // Ensure target element has relative positioning for anchored cursor to work properly
        const computedStyle = window.getComputedStyle(targetElement)
        if (computedStyle.position === 'static') {
          targetElement.style.position = 'relative'
        }
        targetElement.appendChild(cursorElement)
      } else {
        // Fallback to container if target not found
        this.containerTarget.appendChild(cursorElement)
      }
    } else {
      this.containerTarget.appendChild(cursorElement)
    }

    // Create cursor controller
    const cursor = new CursorController(cursorElement, config, this)
    this.cursors.set(cursorId, cursor)

    // Store initial target information for resize handling
    if (options.relativeToTarget) {
      cursor.lastTargetElement = options.relativeToTarget
      cursor.lastRelativeOffset = options.relativeOffset
    }

    // Initialize cursor
    cursor.initialize()

    return cursor
  }

  /**
   * Create the DOM element for a cursor
   * @param {Object} config - Cursor configuration
   * @returns {HTMLElement} - The cursor element
   */
  createCursorElement(config) {
    const element = document.createElement('div')
    const displayMode = config.displayMode || 'cursor'

    element.classList.add(this.hasCursorClass ? this.cursorClass : 'collab__entity')
    element.classList.add(`color__${config.color}`)
    element.classList.add(`collab__entity--${displayMode}`)

    // Add custom className if provided
    if (config.className) {
      const classNames = config.className.split(' ').filter((cls) => cls.trim())
      classNames.forEach((cls) => element.classList.add(cls))
    }

    element.setAttribute('data-cursor-id', config.id)
    element.setAttribute('data-cursor-name', config.name)
    element.setAttribute('data-cursor-display-mode', displayMode)
    // Individual cursor element (not a controller itself)

    // Set basic positioning styles (position will be set by cursor instance)
    element.style.position = 'absolute'
    element.style.zIndex = config.zIndex || '300'
    element.style.pointerEvents = 'none'

    // Create structure based on display mode
    if (config.displayMode === 'avatar' && config.avatarPath) {
      // Map cursor colors to avatar colors
      const avatarColorMap = {
        purple: 'purple',
        blue: 'teal',
        green: 'green',
        pink: 'coral',
        orange: 'yellow',
        teal: 'teal',
        red: 'coral',
        indigo: 'purple',
      }
      const avatarColor = avatarColorMap[config.color] || 'purple'

      // Avatar mode - use colored-ring-avatar structure
      element.innerHTML = `
        <div class="cursor__avatar-wrapper">
          <div class="colored-ring-avatar colored-ring-avatar--${avatarColor}">
            <img
              class="colored-ring-avatar__file cursor__avatar"
              src="https://cdn.sketch.com${config.avatarPath}"
              width="44"
              height="44"
              alt="Profile photo of ${config.name}"
              draggable="false"
            >
          </div>
        </div>
        <div class="cursor__pill" data-cursor-target="pill">
          <span class="cursor__name" data-cursor-target="name">${config.name}</span>
        </div>
      `
    } else {
      // Default cursor mode
      element.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="32px" height="45px" viewBox="0 0 32 45" version="1.1" class="cursor__icon" data-cursor-target="icon">
          <title>Arrow</title>
          <defs>
              <polygon id="path-1" points="904.64446 92.75 908.322376 91.1896407 903.744279 80.4100082 910.888109 80.3737389 894.688109 64.4 894.688109 87.0812441 900.022157 81.9711128"></polygon>
              <filter x="-53.1%" y="-37.1%" width="224.7%" height="173.3%" filterUnits="objectBoundingBox" id="filter-2">
                  <feMorphology radius="2.1" operator="dilate" in="SourceAlpha" result="shadowSpreadOuter1"></feMorphology>
                  <feOffset dx="0" dy="1" in="shadowSpreadOuter1" result="shadowOffsetOuter1"></feOffset>
                  <feGaussianBlur stdDeviation="2" in="shadowOffsetOuter1" result="shadowBlurOuter1"></feGaussianBlur>
                  <feComposite in="shadowBlurOuter1" in2="SourceAlpha" operator="out" result="shadowBlurOuter1"></feComposite>
                  <feColorMatrix values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.15 0" type="matrix" in="shadowBlurOuter1"></feColorMatrix>
              </filter>
          </defs>
          <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
              <g id="Arrow" transform="translate(-888, -56)">
                  <use fill="black" fill-opacity="1" filter="url(#filter-2)" xlink:href="#path-1"></use>
                  <path stroke="#FFFFFF" stroke-width="2.1" d="M893.638109,61.8900741 L913.435358,81.41082 L905.327575,81.4519834 L909.699435,91.7460087 L904.091673,94.1251068 L899.654348,83.7775729 L893.638109,89.541257 L893.638109,61.8900741 Z" fill="#000000" fill-rule="evenodd"></path>
              </g>
          </g>
        </svg>
        <div class="cursor__pill" data-cursor-target="pill">
          <span class="cursor__name" data-cursor-target="name">${config.name}</span>
        </div>
      `
    }

    return element
  }

  /**
   * Remove a cursor
   * @param {string} cursorId - The cursor ID to remove
   */
  removeCursor(cursorId) {
    const cursor = this.cursors.get(cursorId)
    if (cursor) {
      cursor.destroy()
      this.cursors.delete(cursorId)
    }
  }

  /**
   * Clear all cursors
   */
  clearAllCursors() {
    this.cursors.forEach((cursor, id) => {
      cursor.destroy()
    })
    this.cursors.clear()
  }

  /**
   * Get a random name from presets
   * @returns {string} - Random name
   */
  getRandomName() {
    const names = CursorManagerController.CURSOR_PRESETS.names
    return names[Math.floor(Math.random() * names.length)]
  }

  /**
   * Get a random color from presets
   * @returns {string} - Random color
   */
  getRandomColor() {
    const colors = CursorManagerController.CURSOR_PRESETS.colors
    return colors[Math.floor(Math.random() * colors.length)]
  }

  /**
   * Start a demo sequence
   */
  startDemo() {
    // Example demo - spawn cursor with instructions
    setTimeout(() => {
      const cursor = this.spawn({
        name: 'Demo User',
        color: 'purple',
        position: { x: 200, y: 200 },
      })

      cursor.addInstructions([
        { type: 'moveTo', target: '.hero__title', delay: 1000 },
        {
          type: 'action',
          action: 'select',
          target: '.hero__title',
          delay: 500,
        },
        { type: 'moveTo', target: '.button-new', delay: 2000 },
        { type: 'action', action: 'click', target: '.button-new', delay: 500 },
        { type: 'despawn', delay: 3000 },
      ])
    }, 2000)
  }

  /**
   * Debug logging
   * @param {...any} args - Arguments to log
   */
  debug(...args) {
    // Debug logging disabled for production
  }

  /**
   * Get cursor by ID
   * @param {string} cursorId - The cursor ID
   * @returns {CursorController|null} - The cursor controller or null
   */
  getCursor(cursorId) {
    return this.cursors.get(cursorId) || null
  }

  /**
   * Get all cursors
   * @returns {Map} - Map of all cursors
   */
  getAllCursors() {
    return this.cursors
  }

  /**
   * Public API for external usage
   */

  // Action: Spawn cursor (can be called via data-action)
  spawnCursor(event) {
    const options = event.detail || {}
    return this.spawn(options)
  }

  // Action: Clear cursors (can be called via data-action)
  clearCursors() {
    this.clearAllCursors()
  }

  /**
   * Get the positioning context offset
   * @returns {Object} - Offset {x, y}
   */
  getPositioningContextOffset() {
    // Find the cursor container
    const container = this.hasContainerTarget
      ? this.containerTarget
      : this.element.querySelector('.cursor-manager__container')
    if (!container) return { x: 0, y: 0 }

    // Find the first positioned parent
    let parent = container.offsetParent
    if (!parent || parent === document.body) return { x: 0, y: 0 }

    // Get the offset of this positioning context
    const rect = parent.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    return {
      x: rect.left + scrollLeft,
      y: rect.top + scrollTop,
    }
  }

  /**
   * Throttle function execution
   * @param {Function} func - Function to throttle
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Throttled function
   */
  throttle(func, wait) {
    let timeout
    let lastCallTime = 0

    return function executedFunction(...args) {
      const now = Date.now()
      const remaining = wait - (now - lastCallTime)

      if (remaining <= 0) {
        if (timeout) {
          clearTimeout(timeout)
          timeout = null
        }
        lastCallTime = now
        func.apply(this, args)
      } else if (!timeout) {
        timeout = setTimeout(() => {
          lastCallTime = Date.now()
          timeout = null
          func.apply(this, args)
        }, remaining)
      }
    }
  }
}
